/**
 * Canopus: catálogo de planos (canopus_planos, preenchido pelo sync do AFV) e
 * a venda pelo app. Só ofertas SEM reserva de cota — é o que o api-server vende.
 */
import { useMemo, useState } from 'react';
import { useGetList } from 'ra-core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { formatBRL } from '@/lib/format';
import { CanopusVendaModal, type PlanoCanopus } from './CanopusVendaModal';
import { CaixaTabela } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { CabecalhoPagina } from '../bento/ui';

const PRODUTOS = [
  { id: 101, nome: 'Automóveis' },
  { id: 102, nome: 'Motocicletas' },
  { id: 103, nome: 'Imóveis' },
  { id: 104, nome: 'Caminhões' },
  { id: 1001, nome: 'Extra pesados' },
  { id: 108, nome: 'Máquinas e equipamentos' },
  { id: 113, nome: 'Energia solar' },
];

export function CanopusPage() {
  const [produto, setProduto] = useState('101');
  const [busca, setBusca] = useState('');
  const [vender, setVender] = useState<PlanoCanopus | null>(null);

  const { data, isPending, error } = useGetList<PlanoCanopus & { id: number; com_reserva: boolean }>('canopus_planos', {
    filter: { id_tipo_produto: Number(produto), com_reserva: false },
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'valor_credito', order: 'ASC' },
  });

  const planos = useMemo(() => {
    const vistos = new Set<string>();
    const termo = busca.trim().toUpperCase();
    return (data || []).filter((p) => {
      const chave = `${p.codigo_bem}|${p.valor_credito}|${p.tipo_venda}|${p.prazo_meses}`;
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      if (!termo) return true;
      return `${p.codigo_bem} ${p.nome_bem} ${p.valor_credito} ${p.plano} ${p.tipo_venda}`.toUpperCase().includes(termo);
    });
  }, [data, busca]);

  return (
    <div className="space-y-4">
      <CabecalhoPagina titulo="Planos Canopus" descricao="Planos sem reserva de cota, atualizados pelo sync do AFV." />

      <Card className="gap-0 overflow-hidden py-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <Select value={produto} onValueChange={setProduto}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>{PRODUTOS.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="w-72" placeholder="Buscar por código, bem ou valor" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>

        {isPending ? (
          <p className="flex items-center gap-2 px-4 py-6 text-sm"><Spinner className="h-4 w-4" />Carregando planos…</p>
        ) : error ? (
          <p className="px-4 py-6 text-sm text-erro">Não foi possível carregar os planos Canopus.</p>
        ) : planos.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">Nenhum plano sem reserva para esse produto.</p>
        ) : (
          <CaixaTabela>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground [&_th]:h-10 [&_th]:font-medium">
                <tr className="border-b border-border">
                  <th className="px-3">Bem</th>
                  <th className="px-3 text-right">Crédito</th>
                  <th className="px-3 text-right">Prazo</th>
                  <th className="px-3 text-right">1ª parcela</th>
                  <th className="px-3">Plano</th>
                  <th className="px-3">Tipo</th>
                  <th className="px-3" />
                </tr>
              </thead>
              <tbody>
                {planos.map((p) => (
                  <tr key={`${p.codigo_bem}|${p.valor_credito}|${p.tipo_venda}|${p.prazo_meses}`} className="border-b border-border/70 last:border-b-0 hover:bg-linha-hover dark:hover:bg-muted/40">
                    <td className="px-3 py-2.5"><b className="font-mono text-xs">{p.codigo_bem}</b> <span className="text-muted-foreground">{String(p.nome_bem ?? '').replace(/^\S+\s*-\s*/, '')}</span></td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatBRL(Number(p.valor_credito))}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{p.prazo_meses}m</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatBRL(Number(p.primeira_parcela))}</td>
                    <td className="px-3 py-2.5">{p.plano}</td>
                    <td className="px-3 py-2.5">{p.tipo_venda}</td>
                    <td className="px-3 py-2.5 text-right"><Button size="sm" onClick={() => setVender(p)}>Vender</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CaixaTabela>
        )}
      </Card>

      <CanopusVendaModal plano={vender} aoFechar={() => setVender(null)} />
    </div>
  );
}
